import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  JobLikenessRating,
  SoulCastEstimateResponse,
  SoulCastModality,
  SoulCastTier,
  SoulIdentity,
  SoulIdentityQuality,
  SoulIdentityScope,
  SoulPhotoCheckResponse,
} from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useCharacter } from '../hooks/useCharacter.js';
import { useJobs } from '../hooks/useJobs.js';
import { useUI } from '../state/ui.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';
import { Model3DViewer } from './Model3DViewer.js';
import { VoiceRecorderButton } from './VoiceRecorderButton.js';

type CharacterTab = 'soul-id' | 'character' | 'soul-cast' | 'ai-influencer';

interface SoulPhoto {
  id: string;
  name: string;
  url: string;
  file: File;
}

const MAX_TALKING_SECONDS = 60;
const MAX_PERFORMANCE_SECONDS = 30;

function splitLines(value: string): string[] {
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function CharacterScreen() {
  const { t } = useI18n();
  const { toast } = useUI();
  const {
    soulIdentities,
    characters,
    aiInfluencers,
    loading,
    createSoulIdentity,
    uploadSoulPhoto,
    uploadMediaFile,
    startModel3d,
    createCharacter,
    updateCharacter,
    deleteCharacter,
    createAiInfluencer,
    updateAiInfluencer,
    deleteAiInfluencer,
    createSoulCast,
    estimateSoulCast,
    checkSoulPhotos,
  } = useCharacter();
  const { jobs, rateLikeness } = useJobs(true);
  const [tab, setTab] = useState<CharacterTab>('soul-id');
  const [soulName, setSoulName] = useState('');
  const [soulScope, setSoulScope] = useState<SoulIdentityScope>('full-body');
  const [model3dBusyId, setModel3dBusyId] = useState<string | null>(null);
  const [viewing3d, setViewing3d] = useState<SoulIdentity | null>(null);
  const [soulPhotos, setSoulPhotos] = useState<SoulPhoto[]>([]);
  const [soulVoiceFile, setSoulVoiceFile] = useState<File | null>(null);
  const [soulQuality, setSoulQuality] = useState<SoulIdentityQuality>('standard');
  const [soulSelfieFile, setSoulSelfieFile] = useState<File | null>(null);
  const [photoCheck, setPhotoCheck] = useState<SoulPhotoCheckResponse | null>(null);
  const [photoChecking, setPhotoChecking] = useState(false);
  const [soulSubmitting, setSoulSubmitting] = useState(false);
  const [isPhotoDragActive, setIsPhotoDragActive] = useState(false);
  const [characterEditingId, setCharacterEditingId] = useState<string | null>(null);
  const [characterName, setCharacterName] = useState('');
  const [characterDescription, setCharacterDescription] = useState('');
  const [characterSoulId, setCharacterSoulId] = useState('');
  const [characterReferences, setCharacterReferences] = useState('');
  const [castSoulId, setCastSoulId] = useState('');
  const [castCharacterId, setCastCharacterId] = useState('');
  const [castPrompt, setCastPrompt] = useState('');
  const [castTarget, setCastTarget] = useState<SoulCastModality>('image');
  const [castStyle, setCastStyle] = useState('');
  const [castScene, setCastScene] = useState('');
  const [castTier, setCastTier] = useState<SoulCastTier>('budget');
  const [castText, setCastText] = useState('');
  const [castAudioFile, setCastAudioFile] = useState<File | null>(null);
  const [castVideoFile, setCastVideoFile] = useState<File | null>(null);
  const [castSubmitting, setCastSubmitting] = useState(false);
  const [castEnhance, setCastEnhance] = useState(true);
  const [castUpscale, setCastUpscale] = useState(false);
  const [castDuration, setCastDuration] = useState<5 | 10>(5);
  const [castEstimate, setCastEstimate] = useState<SoulCastEstimateResponse | null>(null);
  const [castEstimating, setCastEstimating] = useState(false);
  const [influencerEditingId, setInfluencerEditingId] = useState<string | null>(null);
  const [influencerName, setInfluencerName] = useState('');
  const [influencerBio, setInfluencerBio] = useState('');
  const [influencerSoulId, setInfluencerSoulId] = useState('');
  const [influencerCharacterId, setInfluencerCharacterId] = useState('');
  const [influencerStyle, setInfluencerStyle] = useState('');
  const soulPhotoInputRef = useRef<HTMLInputElement>(null);
  const soulVoiceInputRef = useRef<HTMLInputElement>(null);
  const castAudioInputRef = useRef<HTMLInputElement>(null);
  const castVideoInputRef = useRef<HTMLInputElement>(null);
  const soulSelfieInputRef = useRef<HTMLInputElement>(null);
  const soulPhotosRef = useRef<SoulPhoto[]>([]);
  /** Photo id -> uploaded ref, so a photo checked by the coach is not uploaded twice. */
  const uploadedPhotoRefs = useRef(new Map<string, string>());

  const soulCastJobs = useMemo(
    () =>
      jobs.filter((job) => {
        const input = job.input as Record<string, unknown>;
        return input.section === 'character' && input.feature === 'soul-cast';
      }),
    [jobs],
  );
  const statusLabels = {
    training: t('character.status.training'),
    ready: t('character.status.ready'),
    failed: t('character.status.failed'),
  } as const;
  const jobStatusLabels = {
    pending: t('job.pending'),
    processing: t('job.processing'),
    done: t('job.done'),
    failed: t('job.failed'),
    cancelled: t('job.cancelled'),
  } as const;
  const castModeLabels: Record<SoulCastModality, string> = {
    image: t('character.castModeImage'),
    video: t('character.castModeVideo'),
    talking: t('character.castModeTalking'),
    performance: t('character.castModePerformance'),
  };
  const castNeedsSoul = castTarget === 'talking' || castTarget === 'performance';
  const castLabelOf = (job: { modelId: string; input: Record<string, unknown> }) => {
    const mode = job.input.targetModality;
    if (mode === 'upscale') return t('character.castModeUpscale');
    return castModeLabels[mode as SoulCastModality] ?? job.modelId;
  };
  const castSoul = useMemo(() => {
    const viaCharacter = characters.find((item) => item.id === castCharacterId)?.soulId ?? null;
    const id = castSoulId || viaCharacter;
    return id ? (soulIdentities.find((item) => item.id === id) ?? null) : null;
  }, [castCharacterId, castSoulId, characters, soulIdentities]);

  useEffect(() => {
    soulPhotosRef.current = soulPhotos;
  }, [soulPhotos]);

  useEffect(
    () => () => {
      soulPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
    },
    [],
  );

  function appendSoulPhotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    const nextPhotos = Array.from(files).map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${index}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      url: URL.createObjectURL(file),
      file,
    }));
    setSoulPhotos((prev) => {
      const combined = [...prev, ...nextPhotos];
      const kept = combined.slice(0, 20);
      combined.slice(20).forEach((photo) => URL.revokeObjectURL(photo.url));
      return kept;
    });
  }

  function clearSoulPhotos() {
    setSoulPhotos((prev) => {
      prev.forEach((photo) => URL.revokeObjectURL(photo.url));
      return [];
    });
  }

  function removeSoulPhoto(id: string) {
    setSoulPhotos((prev) => {
      const target = prev.find((photo) => photo.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((photo) => photo.id !== id);
    });
  }

  async function uploadSoulPhotos(): Promise<string[]> {
    const photoUrls: string[] = [];
    for (const photo of soulPhotos) {
      let ref = uploadedPhotoRefs.current.get(photo.id);
      if (!ref) {
        ref = await uploadSoulPhoto(photo.file);
        uploadedPhotoRefs.current.set(photo.id, ref);
      }
      photoUrls.push(ref);
    }
    return photoUrls;
  }

  async function handlePhotoCheck() {
    if (soulPhotos.length === 0) return;
    setPhotoChecking(true);
    try {
      setPhotoCheck(await checkSoulPhotos(await uploadSoulPhotos()));
    } catch (error) {
      toast(error instanceof Error ? error.message : t('common.error'), 'error');
    } finally {
      setPhotoChecking(false);
    }
  }

  async function handleSoulIdentitySubmit() {
    if (!soulName.trim() || soulPhotos.length < 5 || soulPhotos.length > 20) {
      toast(t('character.soulIdPhotoCount'), 'error');
      return;
    }
    setSoulSubmitting(true);
    try {
      const photoUrls = await uploadSoulPhotos();
      const voiceSampleUrl = soulVoiceFile ? (await uploadMediaFile(soulVoiceFile, 'audio')).ref : null;
      const consentSelfieUrl = soulSelfieFile ? await uploadSoulPhoto(soulSelfieFile) : null;
      await createSoulIdentity({
        name: soulName.trim(),
        scope: soulScope,
        photoUrls,
        voiceSampleUrl,
        quality: soulQuality,
        consentSelfieUrl,
      });
    } catch (error) {
      toast(error instanceof Error ? error.message : t('character.soulIdUploadFailed'), 'error');
      return;
    } finally {
      setSoulSubmitting(false);
    }
    setSoulName('');
    setSoulVoiceFile(null);
    setSoulSelfieFile(null);
    setPhotoCheck(null);
    uploadedPhotoRefs.current.clear();
    clearSoulPhotos();
  }

  function applyTemplate(template: 'reel' | 'followers' | 'product' | 'greeting') {
    if (template === 'reel') {
      setCastTarget('video');
      setCastPrompt(t('character.templateReelPrompt'));
      return;
    }
    setCastTarget('talking');
    setCastAudioFile(null);
    setCastText(
      template === 'followers'
        ? t('character.templateFollowersText')
        : template === 'product'
          ? t('character.templateProductText')
          : t('character.templateGreetingText'),
    );
  }

  const castEstimateKey = [
    castSoulId,
    castCharacterId,
    castTarget,
    castTier,
    castUpscale,
    castDuration,
    castTarget === 'talking' ? castText : '',
    castAudioFile ? 'audio' : '',
  ].join('|');
  useEffect(() => {
    if (!castSoulId && !castCharacterId) {
      setCastEstimate(null);
      return undefined;
    }
    if (castTarget === 'talking' && !castText.trim() && !castAudioFile) {
      setCastEstimate(null);
      return undefined;
    }
    let cancelled = false;
    setCastEstimating(true);
    const timer = window.setTimeout(() => {
      estimateSoulCast({
        soulId: castSoulId || undefined,
        characterId: castCharacterId || undefined,
        targetModality: castTarget,
        tier: castTier,
        upscale: castUpscale,
        durationSeconds: castTarget === 'video' ? castDuration : undefined,
        text: castTarget === 'talking' && !castAudioFile ? castText : undefined,
        audioUrl: castAudioFile ? 'attachment:pending' : undefined,
        prompt: 'estimate',
      })
        .then((result) => {
          if (!cancelled) setCastEstimate(result);
        })
        .catch(() => {
          if (!cancelled) setCastEstimate(null);
        })
        .finally(() => {
          if (!cancelled) setCastEstimating(false);
        });
    }, 600);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [castEstimateKey, estimateSoulCast]);

  async function handleStartModel3d(id: string) {
    setModel3dBusyId(id);
    try {
      await startModel3d(id);
      toast(t('character.model3dStarted'), 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : t('character.model3dFailed'), 'error');
    } finally {
      setModel3dBusyId(null);
    }
  }

  async function handleCharacterSubmit() {
    const payload = {
      name: characterName.trim(),
      description: characterDescription.trim() || null,
      soulId: characterSoulId || null,
      referenceImageUrls: splitLines(characterReferences),
    };
    if (!payload.name) return;
    if (characterEditingId) {
      await updateCharacter(characterEditingId, payload);
    } else {
      await createCharacter(payload);
    }
    setCharacterEditingId(null);
    setCharacterName('');
    setCharacterDescription('');
    setCharacterSoulId('');
    setCharacterReferences('');
  }

  async function handleInfluencerSubmit() {
    const payload = {
      name: influencerName.trim(),
      bio: influencerBio.trim() || null,
      soulId: influencerSoulId || null,
      characterId: influencerCharacterId || null,
      style: influencerStyle.trim() || null,
    };
    if (!payload.name) return;
    if (influencerEditingId) {
      await updateAiInfluencer(influencerEditingId, payload);
    } else {
      await createAiInfluencer(payload);
    }
    setInfluencerEditingId(null);
    setInfluencerName('');
    setInfluencerBio('');
    setInfluencerSoulId('');
    setInfluencerCharacterId('');
    setInfluencerStyle('');
  }

  async function handleSoulCastSubmit() {
    if (!castSoulId && !castCharacterId) return;
    if (castNeedsSoul && !castSoul) {
      toast(t('character.castNeedsSoul'), 'error');
      return;
    }
    if (!castNeedsSoul && !castPrompt.trim()) return;
    if (castTarget === 'talking' && !castText.trim() && !castAudioFile) {
      toast(t('character.castNeedsText'), 'error');
      return;
    }
    if (castTarget === 'performance' && !castVideoFile) {
      toast(t('character.castNeedsVideo'), 'error');
      return;
    }
    setCastSubmitting(true);
    try {
      let audioUrl: string | undefined;
      let drivingVideoUrl: string | undefined;
      let durationSeconds: number | undefined;
      if (castTarget === 'talking' && castAudioFile) {
        const upload = await uploadMediaFile(castAudioFile, 'audio');
        if (upload.durationSeconds && upload.durationSeconds > MAX_TALKING_SECONDS) {
          throw new Error(t('character.castTooLong', { max: MAX_TALKING_SECONDS }));
        }
        audioUrl = upload.ref;
        durationSeconds = upload.durationSeconds;
      }
      if (castTarget === 'performance' && castVideoFile) {
        const upload = await uploadMediaFile(castVideoFile, 'video');
        if (upload.durationSeconds && upload.durationSeconds > MAX_PERFORMANCE_SECONDS) {
          throw new Error(t('character.castTooLong', { max: MAX_PERFORMANCE_SECONDS }));
        }
        drivingVideoUrl = upload.ref;
        durationSeconds = upload.durationSeconds;
      }
      await createSoulCast({
        soulId: castSoulId || undefined,
        characterId: castCharacterId || undefined,
        prompt: castPrompt.trim(),
        targetModality: castTarget,
        style: castStyle.trim() || undefined,
        scene: castScene.trim() || undefined,
        tier: castTier,
        text: castTarget === 'talking' && !audioUrl ? castText.trim() : undefined,
        audioUrl,
        drivingVideoUrl,
        durationSeconds: castTarget === 'video' ? castDuration : durationSeconds,
        enhancePrompt: castEnhance,
        upscale: castNeedsSoul ? castUpscale : false,
      });
      toast(t('character.castStarted'), 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : t('common.error'), 'error');
      return;
    } finally {
      setCastSubmitting(false);
    }
    setCastPrompt('');
    setCastText('');
    setCastAudioFile(null);
    setCastVideoFile(null);
  }

  if (loading) {
    return (
      <div className="jobs-loading">
        <span className="spinner" />
      </div>
    );
  }

  return (
    <section className="media-job-screen">
      <div className="character-tabs" role="tablist" aria-label={t('character.soulIdTitle')}>
        {(
          [
            ['soul-id', t('character.tabSoulId')],
            ['character', t('character.tabCharacter')],
            ['soul-cast', t('character.tabSoulCast')],
            ['ai-influencer', t('character.tabAiInfluencer')],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`btn character-tab-btn ${tab === id ? 'btn-primary is-active' : 'btn-ghost'}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'soul-id' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.soulIdTitle')}</h2>
          <div className="character-form-grid">
            <input
              className="field character-input"
              value={soulName}
              onChange={(event) => setSoulName(event.target.value)}
              placeholder={t('character.name')}
            />
          </div>
          <div className="character-scope" role="radiogroup" aria-label={t('character.scopeLabel')}>
            {(
              [
                ['full-body', t('character.scopeFullBody')],
                ['head', t('character.scopeHead')],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={soulScope === value}
                className={`character-scope-option${soulScope === value ? ' is-active' : ''}`}
                onClick={() => setSoulScope(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="settings-hint">
            {soulScope === 'head' ? t('character.scopeHeadHint') : t('character.scopeFullBodyHint')}
          </p>
          <input
            ref={soulPhotoInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(event) => {
              appendSoulPhotos(event.target.files);
              event.target.value = '';
            }}
          />
          <div
            className={`video-upload-card character-upload-card${isPhotoDragActive ? ' is-drag-active' : ''}`}
            role="button"
            tabIndex={0}
            onDragOver={(event) => {
              event.preventDefault();
              setIsPhotoDragActive(true);
            }}
            onDragLeave={() => setIsPhotoDragActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsPhotoDragActive(false);
              appendSoulPhotos(event.dataTransfer.files);
            }}
            onClick={() => soulPhotoInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              soulPhotoInputRef.current?.click();
            }}
          >
            <span className="video-label">{t('character.soulIdTitle')}</span>
            <span className="video-helper">{t('character.soulIdPhotoHint')}</span>
            <span className="character-upload-count">{soulPhotos.length}/20</span>
            {soulPhotos.length > 0 && (
              <ul className="character-photo-grid" onClick={(event) => event.stopPropagation()}>
                {soulPhotos.map((photo) => (
                  <li key={photo.id} className="character-photo-item">
                    <img src={photo.url} alt={photo.name} className="character-photo-thumb" />
                    <button
                      type="button"
                      className="image-attachment-remove"
                      aria-label={t('common.delete')}
                      onClick={() => removeSoulPhoto(photo.id)}
                    >
                      <Icon name="close" size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="settings-hint">{t('character.soulIdPhotoTips')}</p>
          <div className="character-check">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={photoChecking || soulPhotos.length === 0}
              onClick={() => void handlePhotoCheck()}
            >
              <Icon name="check" size={14} />
              {photoChecking ? t('character.checkingPhotos') : t('character.checkPhotos')}
            </button>
            {photoCheck ? (
              <div className={`character-check-result${photoCheck.ok ? '' : ' is-bad'}`} role="status">
                {photoCheck.ok && photoCheck.missing.length === 0 && photoCheck.problems.length === 0 ? (
                  <span>{t('character.photoCheckOk')}</span>
                ) : null}
                {photoCheck.missing.length > 0 ? (
                  <span>{t('character.photoCheckMissing', { items: photoCheck.missing.join(', ') })}</span>
                ) : null}
                {photoCheck.problems.length > 0 ? (
                  <span>{t('character.photoCheckProblems', { items: photoCheck.problems.join('; ') })}</span>
                ) : null}
                <span>{photoCheck.advice}</span>
              </div>
            ) : null}
          </div>
          <div className="character-scope" role="radiogroup" aria-label={t('character.quality')}>
            {(
              [
                ['standard', t('character.qualityStandard')],
                ['max', t('character.qualityMax')],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={soulQuality === value}
                className={`character-scope-option${soulQuality === value ? ' is-active' : ''}`}
                onClick={() => setSoulQuality(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="settings-hint">{t('character.qualityHint')}</p>
          <div className="character-media-field">
            <span className="video-label">{t('character.consentSelfie')}</span>
            <span className="settings-hint">{t('character.consentSelfieHint')}</span>
            <input
              ref={soulSelfieInputRef}
              type="file"
              accept="image/*"
              capture="user"
              className="sr-only"
              onChange={(event) => {
                setSoulSelfieFile(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
            <div className="character-media-row">
              <button type="button" className="btn btn-ghost" onClick={() => soulSelfieInputRef.current?.click()}>
                <Icon name="camera" size={14} />
                {soulSelfieFile ? soulSelfieFile.name : t('character.consentSelfieTake')}
              </button>
              {soulSelfieFile ? (
                <button type="button" className="btn btn-ghost" onClick={() => setSoulSelfieFile(null)}>
                  <Icon name="close" size={12} />
                  {t('character.removeFile')}
                </button>
              ) : null}
            </div>
          </div>
          <div className="character-media-field">
            <span className="video-label">{t('character.voiceSample')}</span>
            <span className="settings-hint">{t('character.voiceSampleHint')}</span>
            <span className="settings-hint">{t('character.recordVoiceScript')}</span>
            <p className="character-script">{t('character.recordVoiceScriptText')}</p>
            <div className="character-media-row">
              <VoiceRecorderButton
                label={t('character.recordVoice')}
                stopLabel={t('character.recordVoiceStop')}
                onRecorded={(file) => setSoulVoiceFile(file)}
                onError={() => toast(t('character.recordVoiceFailed'), 'error')}
              />
            </div>
            <input
              ref={soulVoiceInputRef}
              type="file"
              accept="audio/*"
              className="sr-only"
              onChange={(event) => {
                setSoulVoiceFile(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
            <div className="character-media-row">
              <button type="button" className="btn btn-ghost" onClick={() => soulVoiceInputRef.current?.click()}>
                <Icon name="mic" size={14} />
                {soulVoiceFile ? soulVoiceFile.name : t('character.voiceSamplePick')}
              </button>
              {soulVoiceFile ? (
                <button type="button" className="btn btn-ghost" onClick={() => setSoulVoiceFile(null)}>
                  <Icon name="close" size={12} />
                  {t('character.removeFile')}
                </button>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={soulSubmitting}
            onClick={() => void handleSoulIdentitySubmit()}
          >
            {t('character.createSoulId')}
          </button>
          <div className="character-list">
            {soulIdentities.map((item) => (
              <div key={item.id} className="job-item character-card">
                <div className="character-card-head">
                  <div className="character-card-identity">
                    <div className="character-card-avatar">
                      {item.photoUrls[0] ? (
                        <img
                          src={item.photoUrls[0]}
                          alt={item.name}
                          className="character-card-thumb"
                        />
                      ) : (
                        <Icon name="userRound" size={16} />
                      )}
                    </div>
                    <strong>{item.name}</strong>
                  </div>
                  <span className={`character-status-badge is-${item.status}`}>
                    {statusLabels[item.status]}
                  </span>
                </div>
                <div className="character-meta-row">
                  <span className="character-scope-badge">
                    {item.scope === 'head'
                      ? t('character.scopeBadgeHead')
                      : t('character.scopeBadgeFullBody')}
                  </span>
                  {item.likenessScore !== null && (
                    <span className="character-scope-badge">
                      {t('character.likeness', { score: item.likenessScore })}
                    </span>
                  )}
                  {item.voiceStatus === 'ready' && (
                    <span className="character-scope-badge">
                      <Icon name="mic" size={12} />
                      {t('character.voiceReady')}
                    </span>
                  )}
                  {item.voiceStatus === 'failed' && (
                    <span className="character-scope-badge is-warning" title={item.voiceError ?? undefined}>
                      {t('character.voiceFailed')}
                    </span>
                  )}
                  {item.loraStatus === 'training' && (
                    <span className="character-scope-badge">{t('character.loraTraining')}</span>
                  )}
                  {item.loraStatus === 'ready' && (
                    <span className="character-scope-badge">{t('character.loraReady')}</span>
                  )}
                  {item.loraStatus === 'failed' && (
                    <span className="character-scope-badge is-warning">{t('character.loraFailed')}</span>
                  )}
                  {item.consentStatus === 'verified' && (
                    <span className="character-scope-badge">{t('character.consentVerified')}</span>
                  )}
                  {item.consentStatus === 'failed' && (
                    <span className="character-scope-badge is-warning">{t('character.consentFailed')}</span>
                  )}
                </div>
                {item.status === 'failed' && item.errorMessage ? (
                  <p className="character-model3d-error" role="alert">
                    {item.errorMessage}
                  </p>
                ) : null}
                {item.status === 'ready' && (
                  <div className="character-identity-pack">
                    {item.identityPackUrls.length > 0 ? (
                      <>
                        <span className="settings-hint">{t('character.identityPackHint')}</span>
                        <ul className="character-photo-grid">
                          {item.identityPackUrls.map((url, index) => (
                            <li key={`${item.id}-pack-${index}`} className="character-photo-item">
                              <img src={url} alt={`${item.name} ${index + 1}`} className="character-photo-thumb" />
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <span className="settings-hint">{t('character.identityPackMissing')}</span>
                    )}
                  </div>
                )}
                {item.status === 'ready' && (
                  <div className="character-model3d">
                    {item.model3dStatus === 'processing' && (
                      <p className="character-model3d-status" role="status">
                        <span className="spinner" />
                        {t('character.model3dBuilding')}
                      </p>
                    )}
                    {item.model3dStatus === 'failed' && (
                      <p className="character-model3d-error" role="alert">
                        {t('character.model3dFailed')}
                        {item.model3dError ? `: ${item.model3dError}` : ''}
                      </p>
                    )}
                    <div className="character-card-actions">
                      {item.model3dStatus === 'ready' && item.model3dUrl ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setViewing3d(item)}
                        >
                          <Icon name="cube" size={14} />
                          {t('character.model3dView')}
                        </button>
                      ) : null}
                      {(item.model3dStatus === 'none' || item.model3dStatus === 'failed') && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={model3dBusyId === item.id}
                          onClick={() => void handleStartModel3d(item.id)}
                        >
                          <Icon name="cube" size={14} />
                          {item.model3dStatus === 'failed'
                            ? t('character.model3dRetry')
                            : t('character.model3dCreate')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={Boolean(viewing3d)}
        onClose={() => setViewing3d(null)}
        title={viewing3d?.name}
        closeLabel={t('common.close')}
        className="model3d-modal"
      >
        {viewing3d?.model3dUrl ? <Model3DViewer key={viewing3d.id} url={viewing3d.model3dUrl} /> : null}
      </Modal>

      {tab === 'character' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.characterTitle')}</h2>
          <div className="character-form-grid">
            <input
              className="field character-input"
              value={characterName}
              onChange={(event) => setCharacterName(event.target.value)}
              placeholder={t('character.name')}
            />
            <textarea
              className="field character-textarea"
              value={characterDescription}
              onChange={(event) => setCharacterDescription(event.target.value)}
              placeholder={t('character.description')}
            />
            <select
              className="field character-select"
              value={characterSoulId}
              onChange={(event) => setCharacterSoulId(event.target.value)}
            >
              <option value="">{t('character.noSoulLinked')}</option>
              {soulIdentities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <div className="character-reference-field">
              <textarea
                className="field character-textarea"
                value={characterReferences}
                onChange={(event) => setCharacterReferences(event.target.value)}
                placeholder={t('character.referenceImages')}
              />
              <span className="settings-hint">{t('character.referenceImages')}</span>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleCharacterSubmit()}
          >
            {characterEditingId ? t('common.save') : t('character.createCharacter')}
          </button>
          <div className="character-list">
            {characters.map((item) => (
              <div key={item.id} className="job-item character-card">
                <div className="character-card-head">
                  <div className="character-card-identity">
                    <div className="character-card-avatar">
                      {item.referenceImageUrls[0] ? (
                        <img
                          src={item.referenceImageUrls[0]}
                          alt={item.name}
                          className="character-card-thumb"
                        />
                      ) : (
                        <Icon name="image" size={16} />
                      )}
                    </div>
                    <strong>{item.name}</strong>
                  </div>
                </div>
                {item.description ? (
                  <p className="character-card-copy">{item.description}</p>
                ) : null}
                <div className="character-card-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setCharacterEditingId(item.id);
                      setCharacterName(item.name);
                      setCharacterDescription(item.description ?? '');
                      setCharacterSoulId(item.soulId ?? '');
                      setCharacterReferences(item.referenceImageUrls.join('\n'));
                    }}
                  >
                    <Icon name="edit" size={14} />
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void deleteCharacter(item.id)}
                  >
                    <Icon name="trash" size={14} />
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'soul-cast' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.soulCastTitle')}</h2>
          <div className="character-templates" aria-label={t('character.templates')}>
            {(
              [
                ['reel', t('character.templateReel')],
                ['followers', t('character.templateFollowers')],
                ['product', t('character.templateProduct')],
                ['greeting', t('character.templateGreeting')],
              ] as const
            ).map(([key, label]) => (
              <button key={key} type="button" className="btn btn-ghost" onClick={() => applyTemplate(key)}>
                {label}
              </button>
            ))}
          </div>
          <div className="character-form-grid">
            <select
              className="field character-select"
              value={castSoulId}
              onChange={(event) => setCastSoulId(event.target.value)}
            >
              <option value="">{t('character.pickSoulId')}</option>
              {soulIdentities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <select
              className="field character-select"
              value={castCharacterId}
              onChange={(event) => setCastCharacterId(event.target.value)}
            >
              <option value="">{t('character.pickCharacter')}</option>
              {characters.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <select
              className="field character-select"
              aria-label={t('character.castMode')}
              value={castTarget}
              onChange={(event) => setCastTarget(event.target.value as SoulCastModality)}
            >
              {(Object.keys(castModeLabels) as SoulCastModality[]).map((mode) => (
                <option key={mode} value={mode}>
                  {castModeLabels[mode]}
                </option>
              ))}
            </select>
            {castNeedsSoul && !castSoul ? (
              <p className="settings-hint">{t('character.castNeedsSoul')}</p>
            ) : null}
            {castTarget !== 'image' && (
              <div className="character-scope" role="radiogroup" aria-label={t('character.castTier')}>
                {(
                  [
                    ['budget', t('character.castTierBudget')],
                    ['max', t('character.castTierMax')],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={castTier === value}
                    className={`character-scope-option${castTier === value ? ' is-active' : ''}`}
                    onClick={() => setCastTier(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {castNeedsSoul && (
              <p className="settings-hint">
                {castTarget === 'talking'
                  ? t('character.castTierTalkingHint')
                  : t('character.castTierPerformanceHint')}
              </p>
            )}
            {castTarget === 'video' && (
              <select
                className="field character-select"
                aria-label={t('character.castDuration')}
                value={castDuration}
                onChange={(event) => setCastDuration(Number(event.target.value) === 10 ? 10 : 5)}
              >
                <option value={5}>5 s</option>
                <option value={10}>10 s</option>
              </select>
            )}
            {castTarget === 'talking' && (
              <div className="character-media-field">
                <textarea
                  className="field character-textarea"
                  value={castText}
                  onChange={(event) => setCastText(event.target.value)}
                  placeholder={t('character.castText')}
                  maxLength={1200}
                  disabled={Boolean(castAudioFile)}
                />
                <span className="settings-hint">{t('character.castTextHint')}</span>
                <input
                  ref={castAudioInputRef}
                  type="file"
                  accept="audio/*"
                  className="sr-only"
                  onChange={(event) => {
                    setCastAudioFile(event.target.files?.[0] ?? null);
                    event.target.value = '';
                  }}
                />
                <div className="character-media-row">
                  <button type="button" className="btn btn-ghost" onClick={() => castAudioInputRef.current?.click()}>
                    <Icon name="waveform" size={14} />
                    {castAudioFile ? castAudioFile.name : t('character.castAudio')}
                  </button>
                  {castAudioFile ? (
                    <button type="button" className="btn btn-ghost" onClick={() => setCastAudioFile(null)}>
                      <Icon name="close" size={12} />
                      {t('character.removeFile')}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            {castTarget === 'performance' && (
              <div className="character-media-field">
                <span className="video-label">{t('character.castDrivingVideo')}</span>
                <span className="settings-hint">{t('character.castDrivingVideoHint')}</span>
                <input
                  ref={castVideoInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime"
                  className="sr-only"
                  onChange={(event) => {
                    setCastVideoFile(event.target.files?.[0] ?? null);
                    event.target.value = '';
                  }}
                />
                <div className="character-media-row">
                  <button type="button" className="btn btn-ghost" onClick={() => castVideoInputRef.current?.click()}>
                    <Icon name="video" size={14} />
                    {castVideoFile ? castVideoFile.name : t('character.pickFile')}
                  </button>
                  {castVideoFile ? (
                    <button type="button" className="btn btn-ghost" onClick={() => setCastVideoFile(null)}>
                      <Icon name="close" size={12} />
                      {t('character.removeFile')}
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            <textarea
              className="field character-textarea"
              value={castPrompt}
              onChange={(event) => setCastPrompt(event.target.value)}
              placeholder={castNeedsSoul ? t('character.castActingPrompt') : t('character.prompt')}
            />
            {!castNeedsSoul && (
              <>
                <input
                  className="field character-input"
                  value={castStyle}
                  onChange={(event) => setCastStyle(event.target.value)}
                  placeholder={t('character.style')}
                />
                <input
                  className="field character-input"
                  value={castScene}
                  onChange={(event) => setCastScene(event.target.value)}
                  placeholder={t('character.scene')}
                />
              </>
            )}
            <label className="character-toggle">
              <input
                type="checkbox"
                checked={castEnhance}
                onChange={(event) => setCastEnhance(event.target.checked)}
              />
              <span>{t('character.enhancePrompt')}</span>
            </label>
            {castNeedsSoul && (
              <label className="character-toggle">
                <input
                  type="checkbox"
                  checked={castUpscale}
                  onChange={(event) => setCastUpscale(event.target.checked)}
                />
                <span>{t('character.upscale')}</span>
              </label>
            )}
            <p className="character-estimate" role="status">
              {castEstimating
                ? t('character.estimating')
                : castEstimate
                  ? `${t('character.estimate', { rub: (castEstimate.kopecks / 100).toFixed(0) })}${
                      castEstimate.parts > 1 ? ` (${t('character.estimateParts', { parts: castEstimate.parts })})` : ''
                    }`
                  : ''}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={castSubmitting}
            onClick={() => void handleSoulCastSubmit()}
          >
            {t('character.generateSoulCast')}
          </button>
          <div className="character-list">
            {soulCastJobs.map((job) => (
              <div key={job.id} className={`job-item character-card job-${job.status}`}>
                <div className="character-card-head">
                  <strong>
                    {castLabelOf(job as { modelId: string; input: Record<string, unknown> })}
                    {typeof (job.input as Record<string, unknown>).partCount === 'number' &&
                    Number((job.input as Record<string, unknown>).partCount) > 1
                      ? ` · ${t('character.part', {
                          index: Number((job.input as Record<string, unknown>).partIndex),
                          count: Number((job.input as Record<string, unknown>).partCount),
                        })}`
                      : ''}
                  </strong>
                  <span className={`character-status-badge is-job-${job.status}`}>
                    {jobStatusLabels[job.status]}
                  </span>
                </div>
                <p className="character-card-copy">
                  {String(
                    (job.input as Record<string, unknown>).text ||
                      (job.input as Record<string, unknown>).prompt ||
                      job.modelId,
                  )}
                </p>
                {job.resultUrl ? (
                  <a
                    className="btn btn-ghost character-result-link"
                    href={job.resultUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="external" size={14} />
                    {t('job.result')}
                  </a>
                ) : null}
                {job.status === 'done' ? (
                  <div className="character-rating" role="group" aria-label={t('character.rateUp')}>
                    {(
                      [
                        ['up', 'thumbsUp', t('character.rateUp')],
                        ['down', 'thumbsDown', t('character.rateDown')],
                      ] as const
                    ).map(([value, icon, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`btn btn-ghost${job.likenessRating === value ? ' is-active' : ''}`}
                        aria-pressed={job.likenessRating === value}
                        onClick={() =>
                          void rateLikeness(
                            job.id,
                            job.likenessRating === value ? null : (value as JobLikenessRating),
                          )
                        }
                      >
                        <Icon name={icon} size={14} />
                        {label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'ai-influencer' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.aiInfluencerTitle')}</h2>
          <div className="character-form-grid">
            <input
              className="field character-input"
              value={influencerName}
              onChange={(event) => setInfluencerName(event.target.value)}
              placeholder={t('character.name')}
            />
            <textarea
              className="field character-textarea"
              value={influencerBio}
              onChange={(event) => setInfluencerBio(event.target.value)}
              placeholder={t('character.bio')}
            />
            <select
              className="field character-select"
              value={influencerSoulId}
              onChange={(event) => setInfluencerSoulId(event.target.value)}
            >
              <option value="">{t('character.pickSoulId')}</option>
              {soulIdentities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <select
              className="field character-select"
              value={influencerCharacterId}
              onChange={(event) => setInfluencerCharacterId(event.target.value)}
            >
              <option value="">{t('character.pickCharacter')}</option>
              {characters.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <input
              className="field character-input"
              value={influencerStyle}
              onChange={(event) => setInfluencerStyle(event.target.value)}
              placeholder={t('character.style')}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleInfluencerSubmit()}
          >
            {influencerEditingId ? t('common.save') : t('character.createAiInfluencer')}
          </button>
          <div className="character-list">
            {aiInfluencers.map((item) => (
              <div key={item.id} className="job-item character-card">
                <div className="character-card-head">
                  <div className="character-card-identity">
                    <div className="character-card-avatar">
                      <Icon name="star" size={16} />
                    </div>
                    <strong>{item.name}</strong>
                  </div>
                </div>
                {item.bio ? <p className="character-card-copy">{item.bio}</p> : null}
                <div className="character-card-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setInfluencerEditingId(item.id);
                      setInfluencerName(item.name);
                      setInfluencerBio(item.bio ?? '');
                      setInfluencerSoulId(item.soulId ?? '');
                      setInfluencerCharacterId(item.characterId ?? '');
                      setInfluencerStyle(item.style ?? '');
                    }}
                  >
                    <Icon name="edit" size={14} />
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setTab('soul-cast');
                      setCastSoulId(item.soulId ?? '');
                      setCastCharacterId(item.characterId ?? '');
                      setCastStyle(item.style ?? '');
                    }}
                  >
                    <Icon name="send" size={14} />
                    {t('character.generateContent')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void deleteAiInfluencer(item.id)}
                  >
                    <Icon name="trash" size={14} />
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
